import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

// MUI Components
import {
  IconButton,
  InputAdornment,
  makeStyles,
  Container,
  FormControl,
  Button,
  Box
} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'

// main components
import { MainTable } from '../components/proposals/MainTable'

import { CustomOutlinedInput } from '../styles/mui_custom_components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'

// services
import { getSFUserList, isRunningInIframe } from '../lib/Global'
import { useHistory, useParams } from 'react-router-dom'
import { setIframeOriginatingCompany } from '../lib/Api'
import { websocketActions } from '../store/websocket'
import { ConfirmDialog } from '../components/form/ConfirmDialog'
import { useProposalSearch } from '../components/proposals/useProposalSearch'
import { createProposal } from '../services/ApiService'
import { ProposalStatusBar } from '../components/proposals/StatusBar'

const useStyles = makeStyles(theme => ({
  globalFiltersContainer: {
    height: '55px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    paddingLeft: '48px',
    [theme.breakpoints.down('sm')]: {
      width: 'fit-content',
      float: 'none',
      height: '50px'
    }
  },
  statusBarContainer: {
    height: '55px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    backgroundColor: theme.colors.white,
    padding: '0px',
    [theme.breakpoints.down('sm')]: {
      width: 'fit-content',
      height: 'auto'
    }
  },
  SearchInput: {
    paddingRight: 0,
    width: '230px',
    '& .MuiInputBase-root': {
      '& input': {
        fontSize: '12px'
      }
    },
    '& .MuiInputBase-input': {
      fontSize: '12px'
    },
    height: '32px',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      marginRight: '0'
    }
  },
  resetButton: {
    color: theme.palette.primary.main,
    fontSize: '14px',
    fontWeight: '700',
    textTransform: 'none',
    padding: '0px 30px'
  },
  createButton: {
    color: 'primary',
    fontSize: '13px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '44px'
  },
  tableContainer: {
    margin: '0px',
    padding: '0px',
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.down('xs')]: {
      width: 'fit-content',
      float: 'none'
    }
  },
  dialogContent: {
    display: 'flex !important'
  },
  serviceBox: {
    marginRight: '1px',
    textAlign: 'right'
  },
  reverseCol: {
    display: 'flex',
    backgroundColor: 'white',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column-reverse'
    }
  },
  filtersContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  }
}))

const initFilters = {
  client_name: '',
  client_location: '',
  workorder_number: '',
  submitted_date: '',
  approved_date: '',
  aproved_by: '',
  status: '',
  asc_sort: '',
  desc_sort: ''
}

window.currentSearchIndex = 0

const Proposals = () => {
  const params = useParams()
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const [searchValue, setSearchValue] = useState('')
  const [searchSendValue, setSearchSendValue] = useState('')
  const [searchIndex, setSearchIndex] = useState(0)
  const [displayDownloadError, setDisplayDownloadError] = useState(false)
  const history = useHistory()

  const userStore = useSelector(state => state.auth.user)
  const [companyId, setCompanyId] = useState(
    isRunningInIframe() ? params.id : userStore.userInfo.company_id
  )

  const [openAdvanced, setOpenAdvanced] = useState(false)
  const [selected, setSelected] = useState([])
  const [tablePage, setTablePage] = useState(1)

  const filtersStore = useSelector(state => state.proposals.filters)
  const websocketStore = useSelector(state => state.websocket)

  const [filters, setFilters] = useState({
    ...filtersStore
  })

  const [searchEnabled, setSearchEnabled] = useState(false)

  const { loading, proposals, summary, setProposals, chips, hasMore } =
    useProposalSearch(
      false,
      searchSendValue,
      filters.status,
      filters.client_name,
      filters.client_location,
      filters.proposal_id,
      filters.workorder_number,
      filters.submitted_date,
      filters.approved_date,
      filters.approved_by,
      filters.asc_sort,
      filters.desc_sort,
      companyId,
      30,
      tablePage,
      searchEnabled
    )

  const [searchStatus, setSearchStatus] = useState('')
  const [sfUserList, setSfUserList] = useState([])

  if (isRunningInIframe()) {
    setIframeOriginatingCompany(params.id)
  }

  useEffect(() => {
    const fetchSFUsers = async () => {
      const sfUsers = await getSFUserList(companyId)
      setSfUserList(sfUsers)
    }
    fetchSFUsers()
  }, [companyId])

  useEffect(() => {
    setTablePage(1)
    setSearchEnabled(true)
    setTimeout(() => {
      setSearchEnabled(false)
    }, 500)
  }, [searchSendValue, filters])

  useEffect(() => {
    setFilters(prevData => ({ ...prevData, status: searchStatus }))
    filters.status = searchStatus
  }, [searchStatus])

  useEffect(() => {
    window.currentSearchIndex = searchIndex
    if (searchValue !== searchSendValue) {
      setSearchSendValue(searchValue)
    }
  }, [searchIndex])

  useEffect(() => {
    if (searchValue.length >= 4) {
      const timer = setTimeout(() => {
        setSearchSendValue(searchValue)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [searchValue])

  useEffect(() => {
    if (websocketStore.proposals && websocketStore.proposals.length > 0) {
      const foundIds = []
      websocketStore.proposals.forEach(invoice => {
        const found = proposals.find(
          inv => inv.external_id === invoice.external_id
        )
        if (found) foundIds.push(found.external_id)
      })
      setProposals(records => [
        ...websocketStore.proposals,
        ...records.filter(invoice => !foundIds.includes(invoice.external_id))
      ])
      dispatch(websocketActions.setProposals([]))
    }
  }, [websocketStore.proposals])

  const handleChangeSearch = event => {
    const value = event.target.value
    setSearchValue(value)
  }

  const handleKeyDownSearch = event => {
    if (event.keyCode === 13 && searchValue.length >= 4) {
      setSearchIndex(searchIndex + 1)
    }
  }

  const handleClearGlobalSearch = event => {
    setSearchValue('')
    setSearchIndex(searchIndex + 1)
  }

  const handleBlurSearch = () => {
    if (searchValue.length >= 4) {
      /* Blur delayed if clear exists */
      setTimeout(() => {
        setSearchIndex(searchIndex + 1)
      }, 100)
    }
  }

  const handleCleanFilters = () => {
    setFilters(initFilters)
    setSearchValue('')
    setSearchIndex(searchIndex + 1)
    setCompanyId(
      isRunningInIframe() ? params.id : userStore.userInfo.company_id
    )
    setSelected([])
    setSearchStatus('')
  }

  const validateFilters = () => {
    let value = true
    // eslint-disable-next-line array-callback-return
    Object.keys(initFilters).map(element => {
      if (initFilters[element] !== filters[element]) {
        value = false
      }
      return null
    })
    if (searchValue.length !== 0) {
      value = false
    }
    return value
  }

  const createNewProposal = async () => {
    const newProposal = {
      status: 'draft',
      subcontractor: {
        id: companyId
      }
    }
    const response = await createProposal(newProposal)
    const newProposalId = response.content.id
    if (isRunningInIframe()) {
      history.push({
        pathname:
          '/createProposal-iframe/' + params.id + '/' + params.access_token,
        search: '?id=' + newProposalId
      })
    } else {
      history.push({
        pathname: 'createProposal',
        search: '?id=' + newProposalId
      })
    }
  }

  return (
    <div>
      <div className={classes.reverseCol}>
        <Container
          style={{ overflow: 'hidden' }}
          className={classes.globalFiltersContainer}
          data-testid={'invoice_page'}
        >
          <Button
            variant="contained"
            color="primary"
            className={classes.createButton}
            onClick={() => createNewProposal()}
            data-testid={'new_proposal_button'}
            startIcon={
              <FontAwesomeIcon
                icon="fa-solid fa-circle-plus"
              />
            }
          >
            Create Proposal
          </Button>
          <FormControl variant="outlined" data-testid={'search_field'}>
            <CustomOutlinedInput
              value={searchValue}
              className={classes.SearchInput}
              id="component-outlined"
              placeholder={t('general.labels.search')}
              onChange={handleChangeSearch}
              onKeyDown={handleKeyDownSearch}
              onBlur={handleBlurSearch}
              type="search"
              startAdornment={
                <InputAdornment position="start">
                  <FontAwesomeIcon
                    icon={['far', 'search']}
                    size="xs"
                    color="grey"
                  />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleClearGlobalSearch}>
                    {searchValue.length > 0 ? (
                      <ClearIcon
                        style={{ fontSize: 'small' }}
                        fontSize="small"
                      />
                    ) : (
                      <div />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              autoComplete="off"
            />
          </FormControl>
          <Box className={classes.filtersContainer}>
            {!validateFilters() && (
              <Button
                data-testid={'reset_button'}
                size="small"
                classes={{ root: classes.resetButton }}
                onClick={handleCleanFilters}
              >
                {t('general.labels.reset_filters')}
              </Button>
            )}
          </Box>
        </Container>
        <Container classes={{ root: classes.statusBarContainer }}>
          <ProposalStatusBar
            content={summary}
            setSearchStatus={setSearchStatus}
          />
        </Container>
      </div>
      <Container classes={{ root: classes.tableContainer }}>
        <MainTable
          chips={chips}
          content={proposals}
          openAdvanced={openAdvanced}
          setOpenAdvanced={setOpenAdvanced}
          selected={selected}
          setSelected={setSelected}
          loading={loading}
          hasMore={hasMore}
          setTablePage={setTablePage}
          filters={filters}
          setFilters={setFilters}
          handleCleanFilters={handleCleanFilters}
          validateFilters={validateFilters}
          setSearchEnabled={setSearchEnabled}
          summary={summary}
          totalCount={chips.totalCount}
          sfUserList={sfUserList}
        />
        <ConfirmDialog
          open={displayDownloadError}
          setOpen={setDisplayDownloadError}
          message={t('general.messages.errors.download_error')}
          handleAccept={() => {
            setDisplayDownloadError(false)
          }}
          acceptlabel="Ok"
        />
      </Container>
    </div>
  )
}

export default Proposals
